import {
  Anchor,
  AppFooter,
  AppFooterSmallPrint,
  ColorSchemeToggle,
  Container,
  Eyebrow,
  Grid,
  GridItem,
  Icon,
  Inline,
  PaletteSwitcher,
  Stack,
  Text,
} from "@generalapplications/ui";
import { Rss } from "lucide-react";

import { isListableService, services } from "@/content/services";
import { site } from "@/content/site";
import { routes } from "@/app/common/functions/routes/routes";
import { themeColors, themeStorageKey } from "@/app/common/styles/theme";

import { paletteStorageKey, sitePalettes } from "@/app/common/styles/palette";

type FooterLink = { label: string; to: string };

type FooterColumnProps = {
  /** Names the column's nav landmark. */
  id: string;
  label: string;
  links: readonly FooterLink[];
};

// The label is an Eyebrow, not a heading: the footer sits after <main> on
// every page, so an <h2> here would read as the page's last section.
function FooterColumn({ id, label, links }: FooterColumnProps) {
  return (
    <Stack as="nav" aria-labelledby={id} gap="sm">
      <Eyebrow tone="muted" id={id}>
        {label}
      </Eyebrow>
      {links.map((link) => (
        <Anchor key={link.to} href={link.to} tone="muted" size="sm">
          {link.label}
        </Anchor>
      ))}
    </Stack>
  );
}

export function SiteFooter() {
  // Same gate the prerenderer uses to decide whether /services is crawlable
  // (common/functions/routes/prerenderRoutes.ts). In production isListableService reduces to
  // `status === "published"`, so the link cannot appear while the only thing
  // at /services is the host's noindex 404 catchall. In dev it is always on,
  // which is the intended preview affordance. Evaluated per render rather than
  // at module scope so isDevMode() is not frozen at import time.
  const hasListableServices = services.some(isListableService);

  const exploreLinks: FooterLink[] = [
    { label: "Capabilities", to: routes.capabilities.index },
    { label: "Industries", to: routes.industries.index },
    ...(hasListableServices
      ? [{ label: "Services", to: routes.services.index }]
      : []),
  ];

  const companyLinks: FooterLink[] = [
    { label: "About", to: routes.about },
    { label: "Blog", to: routes.blog.index },
    { label: "Contact", to: routes.contact },
    { label: "Privacy", to: routes.privacy },
  ];

  return (
    <AppFooter>
      <AppFooterSmallPrint>
        {/* Prerendered HTML carries the build year; the client computes the
            current one. They differ for a single day at a year boundary, which
            is what suppressHydrationWarning is for. */}
        <Text as="div" variant="legal">
          <span suppressHydrationWarning>
            © {new Date().getFullYear()} {site.name}
          </span>
        </Text>
        <Text as="div" variant="legal">
          <Anchor href="/feed.xml" tone="inherit" external title="RSS feed">
            <Inline as="span" gap="xs">
              <Icon icon={Rss} size="sm" />
              RSS Feed
            </Inline>
          </Anchor>
        </Text>
      </AppFooterSmallPrint>

      <Grid columns={{ base: 2, md: 4 }} gap="xxl">
        <GridItem span={{ base: 2, md: 1 }}>
          <Stack gap="sm">
            <Text as="span" variant="body" weight="semibold">
              {site.name}
            </Text>
            <Container width="xs" align="start">
              <Text leading="relaxed">{site.tagline}</Text>
            </Container>
          </Stack>
        </GridItem>

        <FooterColumn
          id="footer-explore"
          label="Explore"
          links={exploreLinks}
        />
        <FooterColumn
          id="footer-company"
          label="Company"
          links={companyLinks}
        />

        <GridItem span={{ base: 2, md: 1 }}>
          <Stack gap="md" align={{ base: "start", md: "end" }}>
            <Eyebrow tone="muted">Appearance</Eyebrow>
            <Inline wrap gap="sm">
              {/* Palette switching is a local design-review tool, not a
                  visitor feature. `import.meta.env.DEV`, not isDevMode():
                  Vite substitutes the literal `false` at build time, so the
                  switcher and its palettes tree-shake out of production. */}
              {import.meta.env.DEV && (
                <PaletteSwitcher
                  palettes={sitePalettes}
                  defaultPalette="neutral"
                  storageKey={paletteStorageKey}
                />
              )}
              <ColorSchemeToggle
                storageKey={themeStorageKey}
                themeColors={themeColors}
              />
            </Inline>
          </Stack>
        </GridItem>
      </Grid>
    </AppFooter>
  );
}
